import {
  CoreQuery,
  SubmitF2fComplaintBody,
  SubmitOnlineComplaintBody,
} from "@gen/app-query";
import { useQueryClient } from "@tanstack/react-query";

interface UseSubmitComplaintMutationProps {
  transactionId: number;
  isF2f: boolean;
  successMessage?: string;
}

export function useSubmitComplaintMutation({
  transactionId,
  isF2f,
  successMessage,
}: UseSubmitComplaintMutationProps) {
  const queryClient = useQueryClient();

  const defaultSuccessMessage = `Complaint submitted for transaction: ${transactionId}`;

  const { mutate: submitF2fComplaint, isPending: isF2fComplaintSubmitting } =
    CoreQuery.useAdmin_submitF2fComplaintMutationWithParameters({
      meta: {
        success: successMessage || defaultSuccessMessage,
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
        });
      },
    });

  const {
    mutate: submitOnlineComplaint,
    isPending: isOnlineComplaintSubmitting,
  } = CoreQuery.useAdmin_submitOnlineComplaintMutationWithParameters({
    meta: {
      success: successMessage || defaultSuccessMessage,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
      });
    },
  });

  const submitComplaint = (description: string) => {
    if (isF2f) {
      submitF2fComplaint({
        transaction_id: transactionId,
        submitF2fComplaintBody: SubmitF2fComplaintBody.fromJS({
          description: description.trim(),
        }),
      });
    } else {
      submitOnlineComplaint({
        transaction_id: transactionId,
        submitOnlineComplaintBody: SubmitOnlineComplaintBody.fromJS({
          description: description.trim(),
        }),
      });
    }
  };

  return {
    submitComplaint,
    isLoading: isF2f ? isF2fComplaintSubmitting : isOnlineComplaintSubmitting,
  };
}
